/**
 * The Create Motion form — Motion Name → Industry → Select Assets — shared by the Motions page's
 * `MotionCreateModal` and the STO dashboard's `+ Motion` dialog, so the two cannot drift.
 *
 * Split in two: `useMotionCreateForm` owns the state, the one-shot load and the option logic;
 * `MotionCreateFields` renders the three fields. Each caller owns its own Modal and its own
 * submit, because they do different things with the values (the Motions page creates and
 * navigates; the dashboard creates and then claims an opportunity into the new Motion).
 *
 * DATA decisions, unchanged from when this lived in MotionCreateModal:
 *   - Industry universe = ListIndustries, intersected with the caller's permitted set.
 *   - Asset pool = ONE ListAssets call scoped to origin=ivt_incubated (the IVT incubated slice),
 *     split client-side: industry-anchored assets show only for the chosen Industry; topic-anchored
 *     ones (no industry) are always selectable.
 *   - Industry is @required; assets are optional.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  FormField,
  Input,
  Multiselect,
  Select,
  SpaceBetween,
  type MultiselectProps,
  type SelectProps,
} from '@amzn/awsui-components-react';
import {
  AssetOrigin,
  getIvtClient,
  ListAssetsCommand,
  ListIndustriesCommand,
  type AssetSummary,
} from '../../client/IvtHubClient';
import { scopeIndustriesToAllowed } from '../../client/permissionClient';

export interface MotionCreateValues {
  name: string;
  industry: string;
  assetIds: string[];
}

export interface MotionCreateFormOptions {
  /** When set, only these industries are selectable (permission-scoped). */
  allowedIndustries?: string[] | null;
  /** Pre-selected once the options land, if it is among them. */
  initialIndustry?: string | null;
  /** Pre-selected once the options land; any not in the pool for that industry is dropped. */
  initialAssetIds?: readonly string[];
}

export interface MotionCreateForm {
  name: string;
  setName: (name: string) => void;
  industry: SelectProps.Option | null;
  setIndustry: (industry: SelectProps.Option | null) => void;
  selectedAssets: readonly MultiselectProps.Option[];
  setSelectedAssets: (assets: readonly MultiselectProps.Option[]) => void;
  industryOptions: SelectProps.Option[];
  assetOptions: MultiselectProps.Option[];
  loading: boolean;
  loadError: string | null;
  /** The values to submit, or null while the form is incomplete (no name, or no industry). */
  values: MotionCreateValues | null;
}

export function useMotionCreateForm({
  allowedIndustries,
  initialIndustry = null,
  initialAssetIds = [],
}: MotionCreateFormOptions = {}): MotionCreateForm {
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState<SelectProps.Option | null>(null);
  const [selectedAssets, setSelectedAssets] = useState<readonly MultiselectProps.Option[]>([]);

  const [industries, setIndustries] = useState<string[]>([]);
  const [assets, setAssets] = useState<AssetSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const client = getIvtClient();
        const [indOut, assetOut] = await Promise.all([
          client.send(new ListIndustriesCommand({})),
          client.send(new ListAssetsCommand({ origin: AssetOrigin.IVT_INCUBATED })),
        ]);
        if (cancelled) return;
        setIndustries(
          (indOut.industries ?? [])
            .map((i) => i.industry)
            .filter((n): n is string => !!n)
            .sort((a, b) => a.localeCompare(b, 'en')),
        );
        setAssets(assetOut.assets ?? []);
      } catch (e) {
        if (!cancelled) setLoadError((e as Error).message ?? '加载失败');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Intersect the real industry universe with the permission-allowed set (drops non-industry
  // sh_l4 codes like CROSS/GFD AM + disallowed industries). `allowedIndustries` UNDEFINED = no
  // scope given → every industry; a real array (incl. empty = no options) is fail-closed.
  const industryOptions = useMemo<SelectProps.Option[]>(() => {
    const scoped =
      allowedIndustries == null ? industries : scopeIndustriesToAllowed(industries, allowedIndustries);
    return scoped.map((i) => ({ label: i, value: i }));
  }, [industries, allowedIndustries]);

  // Industry-anchored assets for the chosen Industry, then all topic-anchored ones. Each carries a
  // "{kind} · {name}" sub-label so a UC can be told from a Topic. `AssetSummary.topic` is the
  // TOP-LEVEL Topic name, not the sub-topic. Do NOT use `audience` — incubated assets carry the
  // literal seed value "placeholder" there.
  const assetOptions = useMemo<MultiselectProps.Option[]>(() => {
    const chosen = industry?.value;
    const ucOpts: MultiselectProps.Option[] = [];
    const topicOpts: MultiselectProps.Option[] = [];
    for (const a of assets) {
      if (!a.assetId) continue;
      const label = a.name ?? a.useCase ?? a.assetId;
      if (a.industry) {
        if (chosen && a.industry === chosen) {
          ucOpts.push({
            value: a.assetId,
            label,
            description: a.useCase ? `UC · ${a.useCase}` : undefined,
          });
        }
      } else if (a.topic) {
        topicOpts.push({ value: a.assetId, label, description: `Topic · ${a.topic}` });
      }
    }
    const byLabel = (x: MultiselectProps.Option, y: MultiselectProps.Option) =>
      (x.label ?? '').localeCompare(y.label ?? '', 'en');
    return [...ucOpts.sort(byLabel), ...topicOpts.sort(byLabel)];
  }, [assets, industry]);

  // The initial selection is applied ONCE, when the options it names first exist — later it is
  // the user's. Industry first, since the asset options depend on it.
  const seededIndustry = useRef(false);
  useEffect(() => {
    if (seededIndustry.current || !initialIndustry || industryOptions.length === 0) return;
    seededIndustry.current = true;
    const hit = industryOptions.find((o) => o.value === initialIndustry);
    if (hit) setIndustry(hit);
  }, [industryOptions, initialIndustry]);

  const seededAssets = useRef(false);
  useEffect(() => {
    if (seededAssets.current || initialAssetIds.length === 0) return;
    if (initialIndustry && industry?.value !== initialIndustry) return;
    if (assetOptions.length === 0) return;
    seededAssets.current = true;
    const wanted = new Set(initialAssetIds);
    setSelectedAssets(assetOptions.filter((o) => o.value && wanted.has(o.value)));
  }, [assetOptions, industry, initialIndustry, initialAssetIds]);

  // When Industry changes, prune any selected asset that is no longer an option — an asset from
  // the PREVIOUS industry would otherwise stay "ghost-selected" (out of the list yet still
  // submitted). Pruning against the recomputed options, not a blanket wipe, keeps topic picks.
  useEffect(() => {
    const valid = new Set(assetOptions.map((o) => o.value));
    setSelectedAssets((prev) => {
      const kept = prev.filter((o) => valid.has(o.value));
      return kept.length === prev.length ? prev : kept;
    });
  }, [assetOptions]);

  const values: MotionCreateValues | null =
    name.trim() && industry?.value
      ? {
          name: name.trim(),
          industry: industry.value,
          assetIds: selectedAssets.map((o) => o.value).filter((v): v is string => !!v),
        }
      : null;

  return {
    name,
    setName,
    industry,
    setIndustry,
    selectedAssets,
    setSelectedAssets,
    industryOptions,
    assetOptions,
    loading,
    loadError,
    values,
  };
}

export function MotionCreateFields({
  form,
  disabled = false,
}: {
  form: MotionCreateForm;
  disabled?: boolean;
}) {
  return (
    <SpaceBetween size="l">
      <FormField label="Motion Name">
        <Input
          value={form.name}
          onChange={(e) => form.setName(e.detail.value)}
          placeholder="Placeholder"
          disabled={disabled}
        />
      </FormField>

      {/* Industry is required — see `values`. */}
      <FormField label="Industry">
        <Select
          selectedOption={form.industry}
          onChange={(e) => form.setIndustry(e.detail.selectedOption)}
          options={form.industryOptions}
          placeholder="Choose options"
          disabled={disabled}
          empty="No industries"
        />
      </FormField>

      <FormField
        label={
          <span>
            Select Assets <i>(Optional)</i>
          </span>
        }
      >
        <Multiselect
          selectedOptions={form.selectedAssets}
          onChange={(e) => form.setSelectedAssets(e.detail.selectedOptions)}
          options={form.assetOptions}
          placeholder="Choose options"
          disabled={disabled}
          filteringType="auto"
          empty={form.industry ? 'No assets for this industry' : 'Choose an industry first'}
          tokenLimit={20}
        />
      </FormField>
    </SpaceBetween>
  );
}
